/** Payload presets: the shapes an author would otherwise have to type by hand.
 *
 * A placed object is a def plus a payload, and the payload is where everything
 * interesting lives - what a mine yields, what a box holds, what a seer asks
 * for, when an event fires. The general key/value editor beside this can write
 * any of them, but only if the author already knows the vocabulary, and the
 * vocabulary is documented in three block comments inside the kernel.
 *
 * So a preset is a starting point with the right keys already in it and a
 * sentence saying what it does. The values are deliberately modest - an author
 * changing 1000 to 2000 is doing the job; an author deleting a key he did not
 * expect is not.
 *
 * Every key below is one the kernel actually reads. They are grouped by the
 * file that reads them, so a preset that stops working has one place to look.
 */

import type { DraftPayload } from "./model.js";

export interface PayloadPreset {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  /** Object kinds this preset makes sense on; empty means any. */
  readonly kinds: readonly string[];
  readonly payload: DraftPayload;
}

export const PRESETS: readonly PayloadPreset[] = Object.freeze([
  // ---- reward.ts
  {
    id: "reward.gold",
    label: "Gold",
    description: "Pays gold to whoever collects it.",
    kinds: [],
    payload: { gold: 2000 },
  },
  {
    id: "reward.resource",
    label: "Resource",
    description: "Pays one resource. Change `resource` to wood, ore, mercury, sulfur, crystal or gems.",
    kinds: [],
    payload: { resource: "wood", amount: 10 },
  },
  {
    id: "reward.experience",
    label: "Experience",
    description: "Given to the hero who collects it, scaled by their own Learning.",
    kinds: [],
    payload: { experience: 5000 },
  },
  {
    id: "reward.creatures",
    label: "Creatures",
    description: "Joins the hero's army if a slot is free. `rewardCreature` is a creature id.",
    kinds: [],
    payload: { rewardCreature: "sunspire.spearwarden", rewardCount: 20 },
  },
  {
    id: "reward.artifact",
    label: "Artifact",
    description: "Into the hero's backpack. `artifact` is an artifact id.",
    kinds: [],
    payload: { artifact: "spell_book" },
  },
  {
    id: "reward.primary",
    label: "Primary skill",
    description: "A permanent rise. `primary` is attack, defense, spellPower or knowledge.",
    kinds: [],
    payload: { primary: "attack", primaryAmount: 1 },
  },

  // ---- quests.ts
  {
    id: "quest.resources",
    label: "Quest: bring resources",
    description: "A Oracle's Hut that wants a resource before it pays out.",
    kinds: ["bonus"],
    payload: { quest: "resources", questResource: "gold", questAmount: 5000, gold: 10000 },
  },
  {
    id: "quest.level",
    label: "Quest: reach a level",
    description: "Pays out to a hero who is already this level or above.",
    kinds: ["bonus"],
    payload: { quest: "level", questLevel: 10, experience: 10000 },
  },
  {
    id: "quest.artifact",
    label: "Quest: bring an artifact",
    description: "Takes the artifact and pays out. `questArtifact` is an artifact id.",
    kinds: ["bonus"],
    payload: { quest: "artifact", questArtifact: "spell_book", gold: 8000 },
  },

  // ---- services.ts, timed events
  {
    id: "event.once",
    label: "Timed event: once",
    description:
      "Fires on the first day boundary at or after `day`, for the players named, then "
      + "removes itself. `players` is a comma list of player ids; leave it empty for "
      + "every kingdom. Resources go to the treasury and everything else needs a hero "
      + "standing, so a kingdom with none collects the gold and nothing else.",
    kinds: ["bonus"],
    payload: { service: "event", day: 14, repeatDays: 0, players: "", text: "", gold: 5000 },
  },
  {
    id: "event.repeating",
    label: "Timed event: repeating",
    description:
      "Fires at or after `day` and every `repeatDays` after that. `day: 1` fires on day 2 "
      + "- a game begins on day 1 and the boundary runs on the way out of it. Do not set "
      + "`firedDay`; the kernel writes it to remember the rhythm.",
    kinds: ["bonus"],
    payload: { service: "event", day: 7, repeatDays: 7, players: "", text: "", gold: 1000 },
  },

  // ---- objects.ts
  {
    id: "mine.yield",
    label: "Mine yield",
    description: "What this mine pays its owner each day. `resource` names which.",
    kinds: ["mine"],
    payload: { resource: "gold", amount: 1000 },
  },
  {
    id: "market.stock",
    label: "Black Market stock",
    description:
      "`offers` is the artifact list and `stocked` is whether it has been rolled. "
      + "Both matter: an emptied window and a never-opened one both have an empty "
      + "`offers`, so without `stocked` a sold-out market rolls fresh stock on the "
      + "next visit.",
    kinds: ["bonus"],
    payload: { offers: "", stocked: false },
  },
  {
    id: "guard",
    label: "Guarded",
    description:
      "A stack that has to be beaten before the object can be taken. `creature` is a "
      + "creature id and `count` how many.",
    kinds: [],
    payload: { creature: "neutral.wolf", count: 20 },
  },
]);

export const presetsFor = (kind: string): readonly PayloadPreset[] =>
  PRESETS.filter((p) => p.kinds.length === 0 || p.kinds.includes(kind));

export const findPreset = (id: string): PayloadPreset | null =>
  PRESETS.find((p) => p.id === id) ?? null;

/**
 * Whether the content set has a def a timed event can be placed on.
 *
 * An event object is invisible in the classic and every def in this content set must
 * have a drawn sprite, so there may be none - in which case the preset is still
 * offered on any `bonus` object, and simply does nothing until a def exists.
 * Reported rather than hidden, because a control that silently does nothing is
 * worse than one that says why it cannot.
 */
export function eventDefs(
  mapObjects: ReadonlyMap<string, { readonly kind: string; readonly payload: DraftPayload }>,
): readonly string[] {
  const out: string[] = [];
  for (const [id, def] of mapObjects) {
    if (def.kind === "bonus" && def.payload["service"] === "event") out.push(id);
  }
  return out.sort();
}
